/*
 * @Author: Gao Deng gaodeng@nextmar.com
 * @Date: 2025-04-12 02:45:51
 * @LastEditors: Please set LastEditors
 * @LastEditTime: 2025-10-14 12:15:33
 * @FilePath: /src/components/GuessYouLike/index.tsx
 * @Description: 这是默认设置,请设置`customMade`, 打开koroFileHeader查看配置 进行设置: https://github.com/OBKoro1/koro1FileHeader/wiki/%E9%85%8D%E7%BD%AE
 */
import { fetchRecommendProducts } from '@/api/account';
import logger from '@/utils/client-logger';
import { useTranslations } from '@/i18n/use-translations';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Autoplay, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import ProductCard, { Product } from '../Product/ProductCard';
import WapOnly from '../Responsive/WapOnly';
import WebOnly from '../Responsive/WebOnly';
import SwiperNavigationButtons from '../SwiperNavigation';
import { useSpmDomAttr, useSpmModuleCode } from '@/reporting/spm';

const RecommendList = ({
  title,
  className = '',
  titleClassName = '',
  listClassName = '',
  list = [],
  recommendGroupId,
  renderTitle,
  canSwipe = false,
  divideCount = 8,
  showCorrelation = false,
  showCorrelationCount = 3,
  autoPlay = false,
  showSkeleton = false,
  skeletonCount,
  onMktExp,
  onValidMktExp,
  onMktClick,
  renderItem,
  pagination,
  spmModule,
}: {
  title?: string;
  className?: string;
  recommendGroupId?: number;
  list?: Product[];
  titleClassName?: string;
  listClassName?: string;
  renderTitle?: (title?: string) => React.ReactNode;
  canSwipe?: boolean;
  divideCount?: number; // 轮播时，每组显示的商品数量
  showCorrelation?: boolean;
  showCorrelationCount?: number;
  autoPlay?: boolean; // 是否自动播放
  showSkeleton?: boolean; // 是否显示骨架屏
  skeletonCount?: number; // 骨架屏显示的数量
  onMktExp?: (sdEventParams: any, listData: Product[], index?: number) => void;
  onValidMktExp?: (sdEventParams: any, listData: Product[], index?: number) => void;
  onMktClick?: (sdEventParams: any, listData: Product[], index?: number) => void;
  /** 复用商品详情推荐布局时，允许调用方保留自身商品卡行为与埋点。 */
  renderItem?: (item: Product, index: number) => React.ReactNode;
  /** 显式接入横向分页；未传时保持商品详情等调用方的原有行为。 */
  pagination?: {
    loading: boolean;
    hasMore: boolean;
    onLoadMore: () => Promise<void>;
  };
  /** 商品卡所在楼层的 SPM 模块名（编码表 C 位键）；配合调用侧 SpmScopeProvider 解析收藏/色块坑位，缺省不附加。 */
  spmModule?: string;
}) => {
  const t = useTranslations('account.myWishlist');

  title = title || t('mayAlsoLike');
  // Data SPM DOM 打标（请求头 data-spm=1 开关）：楼层模块码（A.B.C）按调用方传入的
  // spmModule（编码表 C 位模块键）查页面作用域编码表，打在楼层容器；商品卡收藏/主体坑位
  // 由 ProductCard/Wish 在同一作用域内解析。首页动态楼层码与无作用域页面查表不命中，
  // 为 undefined 不打标（表外静默，严禁硬造编码）。
  const getSpmModuleCode = useSpmModuleCode();
  const moduleSpmCode = spmModule ? getSpmModuleCode(spmModule) : undefined;
  const floorDomSpm = useSpmDomAttr(moduleSpmCode);
  const [listData, setListData] = useState<Product[]>(list);
  const [paginationSwiper, setPaginationSwiper] = useState<SwiperType | null>(null);
  const [paginationPending, setPaginationPending] = useState(false);
  const paginationPendingRef = useRef(false);
  const checkedItemCountRef = useRef(-1);

  const requestNextPage = useCallback(() => {
    if (!pagination || pagination.loading || !pagination.hasMore || paginationPendingRef.current) {
      return;
    }
    paginationPendingRef.current = true;
    setPaginationPending(true);
    void pagination
      .onLoadMore()
      .catch((error: unknown) => logger.error(error))
      .finally(() => {
        paginationPendingRef.current = false;
        setPaginationPending(false);
      });
  }, [pagination]);

  useEffect(() => {
    if (
      !canSwipe ||
      !pagination ||
      pagination.loading ||
      paginationPending ||
      !paginationSwiper ||
      paginationSwiper.destroyed ||
      !listData.length ||
      checkedItemCountRef.current === listData.length
    )
      return;
    // 单屏无法翻页时补一次检查；数量不变时不反复请求，避免失败后的请求循环。
    checkedItemCountRef.current = listData.length;
    paginationSwiper.update();
    if (paginationSwiper.isEnd) requestNextPage();
  }, [canSwipe, listData.length, pagination, paginationPending, paginationSwiper, requestNextPage]);

  // skeletonCount 默认等于 divideCount
  const actualSkeletonCount = skeletonCount ?? divideCount;

  const getRecommendProducts = async () => {
    try {
      const res = await fetchRecommendProducts(recommendGroupId);
      if (res.code === 200) {
        if ([7].includes(recommendGroupId)) {
          setListData((res.data as { wish_list?: Product[] }).wish_list || []);
        } else if ([8].includes(recommendGroupId)) {
          setListData((res.data as { search_result_list?: Product[] }).search_result_list || []);
        }
      }
    } catch (error) {
      logger.error(error);
    }
  };

  useEffect(() => {
    if (recommendGroupId) {
      getRecommendProducts();
    }
  }, []);

  useEffect(() => {
    if (list && list.length) {
      setListData(list);
    }
  }, [list]);

  const listDataDivide = useMemo(
    () =>
      listData.reduce((acc, item, index) => {
        if (index % divideCount === 0) {
          acc.push([]);
        }
        acc[acc.length - 1].push(item);
        return acc;
      }, [] as Product[][]),
    [listData, divideCount],
  );

  // 动态计算按钮位置
  const buttonPosition = useMemo(() => {
    const verticalPosition = divideCount === 4 ? '!top-[12vw]' : 'top-1/2 -translate-y-1/2';
    return {
      prev: `left-[80px] ${verticalPosition}`,
      next: `right-[80px] ${verticalPosition}`,
    };
  }, [divideCount]);

  // 判断是否显示骨架屏
  const shouldShowSkeleton = showSkeleton && (!list || list.length === 0);

  // 骨架屏组件 - Web版本
  const SkeletonWeb = () => (
    <div className={`w-full ${className}`}>
      {renderTitle ? (
        renderTitle(title)
      ) : (
        <h2 className={`text-32 text-primary-black font-bb text-center mb-40 ${titleClassName}`}>
          {t('mayAlsoLike')}
        </h2>
      )}
      <ul
        className={`grid gap-14 gap-y-[38px] grid-cols-1 lg:grid-cols-3 sm:grid-cols-2 ${listClassName}`}
      >
        {Array.from({ length: actualSkeletonCount }).map((_, index) => (
          <li key={`skeleton-web-${index}`} className="animate-pulse">
            {/* 图片骨架 */}
            <div className="w-full aspect-[4/3] bg-gray-200 rounded-8 mb-12" />
            {/* 内容区域 pt-8 pl-16 pr-16 */}
            <div className="pt-8 pl-16 pr-16">
              {/* 商品名称和价格容器 pt-6 min-h-[27px] */}
              <div className="pt-6 min-h-[27px] flex items-center">
                {/* 商品名称骨架 - text-16 mr-8 */}
                <div className="h-[16px] bg-gray-200 rounded-4 w-2/5 mr-8" />
                {/* 价格骨架 - text-16 */}
                <div className="h-[16px] bg-gray-200 rounded-4 w-1/5" />
              </div>
              {/* 长名称骨架（可选）- mt-3 text-12 */}
              {<div className="mt-3 h-[12px] bg-gray-200 rounded-4 w-3/4" />}
              {/* 彩色图块骨架 - mt-12 h-[20px] */}
              {showCorrelation && (
                <div className="flex mt-12 h-[20px]">
                  <div className="w-[20px] h-[20px] rounded-full bg-gray-200 mr-8" />
                  <div className="w-[20px] h-[20px] rounded-full bg-gray-200 mr-8" />
                  <div className="w-[20px] h-[20px] rounded-full bg-gray-200 mr-8" />
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );

  // 骨架屏组件 - Wap版本
  const SkeletonWap = () => (
    <div className={`w-full ${className}`}>
      {renderTitle ? (
        renderTitle(title)
      ) : (
        <h2
          className={`text-16 text-primary-black font-bb justify-center flex items-center ${titleClassName}`}
        >
          <div className="h-[1px] bg-[linear-gradient(90deg,rgba(234,236,239,0),#d0d4db)] flex-1 mr-8 max-w-[128px]" />
          {title}
          <div className="h-[1px] bg-[linear-gradient(-90deg,rgba(234,236,239,0),#d0d4db)] flex-1 ml-8 max-w-[128px]" />
        </h2>
      )}
      <ul
        className={`grid gap-[8px] grid-cols-2 lg:grid-cols-3 sm:grid-cols-3 pt-16 pl-8 pr-8 ${listClassName}`}
      >
        {Array.from({ length: actualSkeletonCount }).map((_, index) => (
          <li key={`skeleton-wap-${index}`} className="animate-pulse mb-40">
            {/* 图片骨架 */}
            <div className="w-full aspect-square bg-gray-200 rounded-12 mb-8" />
            {/* 商品名称骨架 - text-12 mb-6 */}
            <div className="h-[12px] bg-gray-200 rounded-4 mb-6 w-4/5" />
            {/* 价格骨架 - text-14 leading-[17px] mt-6 */}
            <div className="h-[17px] bg-gray-200 rounded-4 mt-6 w-2/3" />
            {/* 彩色图块骨架 - mt-12 h-[20px] */}
            {showCorrelation && (
              <div className="flex mt-12 h-[20px]">
                <div className="w-[20px] h-[20px] rounded-full bg-gray-200 mr-8" />
                <div className="w-[20px] h-[20px] rounded-full bg-gray-200 mr-8" />
                <div className="w-[20px] h-[20px] rounded-full bg-gray-200 mr-8" />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );

  // 骨架屏组件 - Swipe版本
  const SkeletonSwipe = () => (
    <div className="px-[45px] relative">
      {renderTitle ? (
        renderTitle(title)
      ) : (
        <h2 className={`text-32 text-primary-black font-bb text-center mb-40 ${titleClassName}`}>
          {t('mayAlsoLike')}
        </h2>
      )}
      <ul className="grid grid-cols-4 gap-x-14 gap-y-20">
        {Array.from({ length: actualSkeletonCount }).map((_, index) => (
          <li key={`skeleton-swipe-${index}`} className="animate-pulse">
            {/* 图片骨架 */}
            <div className="w-full aspect-[4/3] bg-gray-200 rounded-8 mb-12" />
            {/* 内容区域 pt-8 pl-16 pr-16 */}
            <div className="pt-8 pl-16 pr-16">
              {/* 商品名称和价格容器 pt-6 min-h-[27px] */}
              <div className="pt-6 min-h-[27px] flex items-center">
                {/* 商品名称骨架 - text-16 mr-8 */}
                <div className="h-[16px] bg-gray-200 rounded-4 w-2/5 mr-8" />
                {/* 价格骨架 - text-16 */}
                <div className="h-[16px] bg-gray-200 rounded-4 w-1/5" />
              </div>
              {/* 长名称骨架 - mt-3 text-12 */}
              <div className="mt-3 h-[12px] bg-gray-200 rounded-4 w-3/4" />
              {/* 彩色图块骨架 - mt-12 h-[20px] */}
              {showCorrelation && (
                <div className="flex mt-12 h-[20px]">
                  <div className="w-[20px] h-[20px] rounded-full bg-gray-200 mr-8" />
                  <div className="w-[20px] h-[20px] rounded-full bg-gray-200 mr-8" />
                  <div className="w-[20px] h-[20px] rounded-full bg-gray-200 mr-8" />
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );

  // 如果需要显示骨架屏
  if (shouldShowSkeleton) {
    if (canSwipe) {
      return <SkeletonSwipe />;
    }
    return (
      <>
        <WebOnly>
          <SkeletonWeb />
        </WebOnly>
        <WapOnly>
          <SkeletonWap />
        </WapOnly>
      </>
    );
  }

  if (canSwipe && listData.length) {
    return (
      <div className="px-[45px] relative swiper-nav-container" {...floorDomSpm}>
        {renderTitle ? (
          renderTitle(title)
        ) : (
          <h2 className={`text-32 text-primary-black font-bb text-center mb-40 ${titleClassName}`}>
            {t('mayAlsoLike')}
          </h2>
        )}
        <Swiper
          onSwiper={pagination ? setPaginationSwiper : undefined}
          onReachEnd={pagination ? requestNextPage : undefined}
          spaceBetween={10}
          slidesPerView={1}
          loop={false}
          autoplay={
            autoPlay
              ? {
                  delay: 3000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }
              : false
          }
          speed={300}
          modules={[Navigation, Autoplay]}
          navigation={{
            prevEl: '.guess-you-like-prev',
            nextEl: '.guess-you-like-next',
          }}
        >
          {listDataDivide.map((itemGroup, groupIndex) => (
            <SwiperSlide key={groupIndex}>
              <ul className="grid grid-cols-4 gap-x-14 gap-y-20">
                {itemGroup.map((item, itemIndex) => {
                  const actualIndex = groupIndex * divideCount + itemIndex;
                  return (
                    <li key={item.id}>
                      {renderItem ? (
                        renderItem(item, actualIndex)
                      ) : (
                        <ProductCard
                          product={item}
                          index={actualIndex}
                          showCorrelation={showCorrelation}
                          showCorrelationCount={showCorrelationCount}
                          spmModule={spmModule}
                          productSpmCnt={
                            spmModule === 'recommended_products' && moduleSpmCode
                              ? `${moduleSpmCode}.product_${String(actualIndex + 1).padStart(3, '0')}`
                              : undefined
                          }
                          handelClick={(_index, product) => {
                            onMktClick?.(product as any, listData, actualIndex);
                          }}
                          onInView={(_index, product) => {
                            onMktExp?.(product as any, listData, actualIndex);
                          }}
                          onValidExposure={(_index, product) => {
                            onValidMktExp?.(product as any, listData, actualIndex);
                          }}
                          frameWidth="70%"
                          showTryOn={false}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* 导航按钮 */}
        <SwiperNavigationButtons
          navigationPrefix="guess-you-like"
          prevButtonPosition={buttonPosition.prev}
          nextButtonPosition={buttonPosition.next}
        />
      </div>
    );
  }
  if (listData.length) {
    return (
      <>
        <WebOnly>
          <div className={`w-full ${className}`} {...floorDomSpm}>
            {renderTitle ? (
              renderTitle(title)
            ) : (
              <h2
                className={`text-32 text-primary-black font-bb text-center mb-40 ${titleClassName}`}
              >
                {t('mayAlsoLike')}
              </h2>
            )}
            <ul
              className={`grid gap-14 gap-y-[38px] grid-cols-1 lg:grid-cols-3 sm:grid-cols-2 ${listClassName}`}
            >
              {listData.map((item, index) => (
                <li key={item.id}>
                  {renderItem ? (
                    renderItem(item, index)
                  ) : (
                    <ProductCard
                      product={item}
                      showTryOn={false}
                      index={index}
                      showCorrelation={showCorrelation}
                      showCorrelationCount={showCorrelationCount}
                      spmModule={spmModule}
                      productSpmCnt={
                        spmModule === 'recommended_products' && moduleSpmCode
                          ? `${moduleSpmCode}.product_${String(index + 1).padStart(3, '0')}`
                          : undefined
                      }
                      handelClick={(_index, product) => {
                        onMktClick?.(product as any, listData, index);
                      }}
                      frameWidth="70%"
                      onInView={(_index, product) => {
                        onMktExp?.(product as any, listData, index);
                      }}
                      onValidExposure={(_index, product) => {
                        onValidMktExp?.(product as any, listData, index);
                      }}
                    />
                  )}
                </li>
              ))}
            </ul>
          </div>
        </WebOnly>
        <WapOnly>
          <div className={`w-full ${className}`} {...floorDomSpm}>
            {renderTitle ? (
              renderTitle(title)
            ) : (
              <h2
                className={`text-16 text-primary-black font-bb justify-center flex items-center ${titleClassName}`}
              >
                <div className="h-[1px] bg-[linear-gradient(90deg,rgba(234,236,239,0),#d0d4db)] flex-1 mr-8 max-w-[128px]" />
                {title}
                <div className="h-[1px] bg-[linear-gradient(-90deg,rgba(234,236,239,0),#d0d4db)] flex-1 ml-8 max-w-[128px]" />
              </h2>
            )}
            <ul
              className={`grid gap-[8px] grid-cols-2 lg:grid-cols-3 sm:grid-cols-3 pt-16 pl-8 pr-8 ${listClassName}`}
            >
              {listData.map((item, index) => (
                <li key={item.id}>
                  {renderItem ? (
                    renderItem(item, index)
                  ) : (
                    <ProductCard
                      product={item}
                      className="mb-40"
                      showTryOn={false}
                      index={index}
                      spmModule={spmModule}
                      productSpmCnt={
                        spmModule === 'recommended_products' && moduleSpmCode
                          ? `${moduleSpmCode}.product_${String(index + 1).padStart(3, '0')}`
                          : undefined
                      }
                      handelClick={(_index, product) => {
                        onMktClick?.(product as any, listData, index);
                      }}
                      onInView={(_index, product) => {
                        onMktExp?.(product as any, listData, index);
                      }}
                      onValidExposure={(_index, product) => {
                        onValidMktExp?.(product as any, listData, index);
                      }}
                      isSquare
                      frameWidth="70%"
                      imageClassName="rounded-12 aspect-square"
                      productInfoClassName="pl-[3px] pr-[3px]"
                      showCorrelation={showCorrelation}
                      showCorrelationCount={showCorrelationCount}
                    />
                  )}
                </li>
              ))}
            </ul>
          </div>
        </WapOnly>
      </>
    );
  }
  return <></>;
};

export default RecommendList;
